
import {
  defineComponent,
  SetupContext,
  ref,
  watch,
  onBeforeUnmount,
} from '@vue/composition-api';

interface Props {
  currentSlide: number;
  totalSlides: number;
  duration: number;
  maxWidth: string;
  paused: boolean;
}

export default defineComponent({
  name: 'FCSwiperProgressBar',
  props: {
    currentSlide: {
      type: Number,
      required: true,
    },
    totalSlides: {
      type: Number,
      required: true,
    },
    duration: {
      type: Number,
      default: 5000,
    },
    maxWidth: {
      type: String,
      default: '200px',
    },
    paused: {
      type: Boolean,
      default: false,
    },
  },
  setup(props: Props, { emit }: SetupContext) {
    const slideProgress = ref(0);
    let animationFrame: number | null = null;
    let startTime: number | null = null;

    const startAnimation = () => {
      // Cancel any existing animation
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      slideProgress.value = 0;
      startTime = Date.now();

      const animate = () => {
        if (!startTime) return;

        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / props.duration) * 100, 100);

        slideProgress.value = progress;

        if (progress < 100) {
          animationFrame = requestAnimationFrame(animate);
        }
      };

      animationFrame = requestAnimationFrame(animate);
    };

    const stopAnimation = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
      slideProgress.value = 0;
      startTime = null;
    };

    // Watch for slide changes and restart animation
    watch(() => props.currentSlide, () => {
      if (!props.paused) {
        startAnimation();
      }
    }, { immediate: true });

    // Watch for paused state changes
    watch(() => props.paused, (isPaused) => {
      if (isPaused) {
        stopAnimation();
      } else {
        startAnimation();
      }
    });

    // Cleanup on unmount
    onBeforeUnmount(() => {
      stopAnimation();
    });

    const handleSlideClick = (index: number) => {
      if (index !== props.currentSlide) {
        emit('slide-click', index);
      }
    };

    return {
      slideProgress,
      handleSlideClick,
    };
  },
});
